import React, {PureComponent} from "react";
import * as Sentry from '@sentry/browser';
import jwt_decode from "jwt-decode";
import ApiConfig from "../../ApiConfig";
import Api from "../../Api";

const {Provider, Consumer: AuthConsumer} = React.createContext({
    isAuthorized: false,
    adminPrivileges: false,
    requires2FASetup: false,
    token: null,
    accountAccessSettings: null,
    globalPrivileges: null
});

class AuthProvider extends PureComponent {

    constructor(props) {
        super(props);

        // Синхронная инициализация из storage — предотвращает redirect на /login при перезагрузке
        // Токен всегда в localStorage (доступен всем вкладкам).
        // Без "Запомнить меня" — sessionStorage маркер SESSION_ACTIVE контролирует сессию:
        // при закрытии браузера маркер пропадёт → следующий запуск очистит токен.
        let token = localStorage.getItem("AUTH_TOKEN");
        let isRemember = localStorage.getItem("IS_REMEMBER") === "Y";

        // Без "Запомнить меня": проверяем, что сессия браузера ещё жива
        if (token && !isRemember) {
            if (!sessionStorage.getItem("SESSION_ACTIVE")) {
                // Браузер был закрыт и открыт заново — очищаем токен
                localStorage.removeItem("AUTH_TOKEN");
                token = null;
            }
        }

        if (token) {
            try {
                const tokenDecode = jwt_decode(token);
                // Маркер сессии для новых вкладок (sessionStorage копируется при middle-click,
                // а для вкладок открытых вручную — устанавливаем здесь)
                sessionStorage.setItem("SESSION_ACTIVE", "Y");
                // Restore Sentry user context from saved token
                Sentry.setUser({
                    id: tokenDecode.username || 'unknown',
                    username: tokenDecode.username,
                    admin: !!tokenDecode.adminPrivileges,
                });

                this.state = {
                    isAuthorized: true,
                    token: token,
                    isRemember: isRemember,
                    adminPrivileges: tokenDecode.adminPrivileges,
                    forcedPasswordChange: tokenDecode.forcedPasswordChange,
                    requires2FASetup: tokenDecode.requires2FASetup,
                    accountAccessSettings: tokenDecode.accountAccessSettings,
                    globalPrivileges: tokenDecode.globalPrivileges != null ? tokenDecode.globalPrivileges : null
                };
            } catch (error) {
                Sentry.captureException(error);
                console.error("Invalid token:", error);
                localStorage.removeItem("AUTH_TOKEN");
                localStorage.removeItem("IS_REMEMBER");
                sessionStorage.removeItem("SESSION_ACTIVE");
                this.state = this._defaultState();
            }
        } else {
            this.state = this._defaultState();
        }
    }

    _defaultState() {
        return {
            isAuthorized: false,
            adminPrivileges: false,
            forcedPasswordChange: false,
            requires2FASetup: false,
            isRemember: false,
            token: null,
            accountAccessSettings: null,
            globalPrivileges: null
        };
    }

    authorize = (token, isRemember) => {
        const tokenDecode = jwt_decode(token);

        // Токен всегда в localStorage — доступен всем вкладкам
        localStorage.setItem("AUTH_TOKEN", token);
        // Маркер активной сессии — пропадает при закрытии браузера
        sessionStorage.setItem("SESSION_ACTIVE", "Y");

        if (isRemember) {
            localStorage.setItem("IS_REMEMBER", "Y");
        } else {
            localStorage.removeItem("IS_REMEMBER");
        }

        // Set Sentry user context
        Sentry.setUser({
            id: tokenDecode.username || 'unknown',
            username: tokenDecode.username,
            admin: !!tokenDecode.adminPrivileges,
        });

        this.setState({
            isAuthorized: true,
            token: token,
            isRemember: !!isRemember,
            adminPrivileges: tokenDecode.adminPrivileges,
            forcedPasswordChange: tokenDecode.forcedPasswordChange,
            requires2FASetup: tokenDecode.requires2FASetup,
            accountAccessSettings: tokenDecode.accountAccessSettings,
            globalPrivileges: tokenDecode.globalPrivileges != null ? tokenDecode.globalPrivileges : null
        });
    }

    logout = () => {
        if (this._permissionsInterval) {
            clearInterval(this._permissionsInterval);
        }
        Sentry.setUser(null);
        this.setState({token: false, isAuthorized: false, adminPrivileges: false, globalPrivileges: null, accountAccessSettings: null});
        localStorage.removeItem("AUTH_TOKEN");
        localStorage.removeItem("IS_REMEMBER");
        sessionStorage.removeItem("SESSION_ACTIVE");
    }

    remember = () => {
        this.setState({isRemember: !this.state.isRemember});
    }

    _refreshPermissions = () => {
        new Api({token: this.state.token}).getPermissions().then(data => {
            if (data && data.accountAccessSettings) {
                const updates = {
                    accountAccessSettings: data.accountAccessSettings,
                    globalPrivileges: data.globalPrivileges != null ? data.globalPrivileges : null,
                    requires2FASetup: !!data.requires2FASetup,
                    forcedPasswordChange: !!data.forcedPasswordChange,
                };
                // Права администратора тоже с сервера: из токена снятые права
                // держали админское меню на экране до конца его жизни (неделю).
                if (data.adminPrivileges !== undefined) {
                    updates.adminPrivileges = data.adminPrivileges;
                }
                this.setState(updates);
            }
        }).catch((err) => {
            Sentry.captureException(err);
            // Если не удалось — используем текущие из JWT
        });
    }

    componentDidMount() {
        ApiConfig.setLogoutCallback(this.logout);

        // Подгружаем свежие права если авторизованы
        if (this.state.isAuthorized && this.state.token) {
            this._refreshPermissions();
        }

        // Периодическое обновление прав каждые 2 минуты
        this._permissionsInterval = setInterval(() => {
            if (this.state.isAuthorized && this.state.token) {
                this._refreshPermissions();
            }
        }, 2 * 60 * 1000);
    }

    componentWillUnmount() {
        if (this._permissionsInterval) {
            clearInterval(this._permissionsInterval);
        }
    }

    clear2FASetupFlag = () => {
        this.setState({requires2FASetup: false});
    }

    render() {
        const {isAuthorized} = this.state
        const {adminPrivileges} = this.state
        const {forcedPasswordChange} = this.state
        const {requires2FASetup} = this.state
        const {isRemember} = this.state
        const {token} = this.state
        const {accountAccessSettings} = this.state
        const {globalPrivileges} = this.state

        return (
            <Provider value={{
                isAuthorized,
                adminPrivileges,
                forcedPasswordChange,
                requires2FASetup,
                accountAccessSettings,
                globalPrivileges,
                isRemember,
                token,
                authorize: this.authorize,
                logout: this.logout,
                remember: this.remember,
                clear2FASetupFlag: this.clear2FASetupFlag
            }}>
                {this.props.children}
            </Provider>
        )
    }
}

export function withAuth(WrappedComponent) {

    return class AuthHOC extends PureComponent {
        render() {
            return (
                <AuthConsumer>
                    {
                        contextProps => (<WrappedComponent {...contextProps} {...this.props}/>)
                    }
                </AuthConsumer>
            )
        }
    }
}

export {AuthProvider}


// WEBPACK FOOTER //
// ./src/hoc/Auth/index.js